import moment, { Moment } from "moment";
import "moment-duration-format";
import { format } from "date-fns/format";
import { isValid } from "date-fns/isValid";
import { parse } from "date-fns/parse";
import { startOfToday } from "date-fns/startOfToday";

const dateMask = "DD.MM.YYYY";
const dateTimeMask = "DD.MM.YYYY HH:mm";
const withoutTzMask = "YYYY-MM-DDTHH:mm:ss";
const timezoneOffsetPattern = /(?:Z|[+-]\d{2}:?\d{2})$/;

export function formatDate(value?: string | Date | Moment | null) {
    return value ? moment(value).format("YYYY-MM-DD") : undefined;
}

export function formatDateForDocumentTag(value?: string | Date | Moment | null) {
    return value ? moment(value).format(dateMask) : undefined;
}

function normalizeUtcOffset(utcOffset?: string) {
    if (!utcOffset) {
        return "";
    }

    const match = utcOffset.match(/([+-])?(\d{1,2}):?(\d{2})/);
    if (!match) {
        return "";
    }

    const sign = match[1] || "+";
    const hours = match[2].padStart(2, "0");
    const minutes = match[3];
    return `${sign}${hours}:${minutes}`;
}

export function timeInTimezone(momentTime: Moment, offset: string) {
    return moment(momentTime.toISOString()).utcOffset(offset).format();
}

export function momentDateInOffset(momentTime: Moment, offset: string) {
    const time = moment(momentTime.toISOString()).utcOffset(offset).format(dateMask);
    return moment(time, dateMask);
}

export function utcTimeInOffset(timeString: string, utcOffset: string) {
    return moment(timeString)
        .utcOffset(utcOffset.charAt(0) === "-" ? utcOffset : `+${utcOffset}`, true)
        .utc()
        .format();
}

export function plainDate(timeString?: string | Date | Moment) {
    if (!timeString || timeString === "null" || timeString === "Invalid date") {
        return "";
    }

    if (moment.isMoment(timeString)) {
        return timeString.isValid() ? timeString.format(withoutTzMask) : "";
    }

    if (timeString instanceof Date) {
        const parsedDate = moment(timeString);
        return parsedDate.isValid() ? parsedDate.format(withoutTzMask) : "";
    }

    if (typeof timeString === "string") {
        const parsedDate = moment.parseZone(timeString, moment.ISO_8601, true);
        return parsedDate.isValid() ? parsedDate.format(withoutTzMask) : "";
    }

    return "";
}

export function dateTimeInUtcOffset(timeString?: string | Date | Moment, utcOffset?: string) {
    const offset = normalizeUtcOffset(utcOffset);
    if (!timeString || !offset || timeString === "null" || timeString === "Invalid date") {
        return "";
    }

    if (moment.isMoment(timeString)) {
        return timeString.isValid() ? timeString.clone().utcOffset(offset).format() : "";
    }

    if (timeString instanceof Date) {
        const parsedDate = moment(timeString);
        return parsedDate.isValid() ? parsedDate.utcOffset(offset).format() : "";
    }

    if (typeof timeString === "string") {
        const parsedDate = moment.parseZone(timeString, moment.ISO_8601, true);
        if (!parsedDate.isValid()) {
            return "";
        }

        if (!timezoneOffsetPattern.test(timeString)) {
            return `${parsedDate.format(withoutTzMask)}${offset}`;
        }

        return parsedDate.utcOffset(offset).format();
    }

    return "";
}

export function timeOffset(time?: string) {
    if (!time) {
        return "";
    }
    return moment.parseZone(time).format("Z");
}

export function dateToView(date: string, mask = dateMask) {
    const formatted = moment.parseZone(date).format(mask);
    return formatted === "Invalid date" ? "-" : formatted;
}

export function dateToLocalView(date: string, mask = dateMask) {
    const formatted = moment(date).format(mask);
    return formatted === "Invalid date" ? "-" : formatted;
}

export function humanizedTimeDiff(timeStart: string, timeEnd: string) {
    if (!timeStart || !timeEnd) {
        return null;
    }
    const timeDiff = moment.duration(moment(timeStart).diff(moment(timeEnd))).format("d.hh:mm:ss", { trim: false });
    return humanizedTimeRange(timeDiff);
}

export function dateTimeToView(date: string, mask = dateTimeMask) {
    if (date && date.length) {
        const formatted = moment.parseZone(date).format(mask);
        return formatted === "Invalid date" ? "-" : formatted;
    }
    return "-";
}

export function dateTimeToIso(date: string, mask = dateTimeMask) {
    return date && date.length ? moment(date, mask).format() : "";
}

export function compareDays(prevDay: string, nextDay: string) {
    if (prevDay === "") {
        return true;
    }
    return moment(nextDay).isAfter(prevDay, "day");
}

export const hourToTimeSpan = (time: number) => {
    const fixedTime = Number(time.toFixed(0));
    const minutes = parseInt(String(fixedTime / 60), 10);
    const seconds = parseInt(String(fixedTime % 60));
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

export function minuteToTimeSpan(minute: number) {
    const hour = Math.floor(minute / 60);
    const rest = minute % 60;
    const minutes = rest > 9 ? rest : `0${rest}`;
    return `${hour}:${minutes}`;
}

export function secondToTimeSpan(second: number) {
    const minute = Math.floor(second / 60);
    const hour = Math.floor(minute / 60);
    const rest = minute % 60;
    const minutes = rest > 9 ? rest : `0${rest}`;
    return `${hour}ч. ${minutes}м.`;
}

export function secondToDateSpan(second: number) {
    let isNegative = false;
    if (second < 0) {
        second = Math.abs(second);
        isNegative = true;
    }
    const minute = Math.floor(second / 60);
    const hour = Math.floor(minute / 60);
    const days = Math.floor(hour / 24);
    const restMinutes = minute % 60;
    const restHours = hour % 24;
    const minutes = restMinutes > 9 ? restMinutes : `0${restMinutes}`;
    let res = `${restHours}ч. ${minutes}м.`;
    if (days) {
        res = `${days}д. ${res}`;
    }
    return `${isNegative ? "-" : ""}${res}`;
}

export function humanizedSecondsRange(seconds: number, format = "d[д.] h[ч.] m[м.]") {
    return moment.duration(seconds, "seconds").format(format, { trim: "both mid" });
}

export function humanizedTimeRange(timeStr: string, format = "d[д.] h[ч.] m[м.]") {
    return moment.duration(timeStr).format(format, { trim: "both mid" });
}

export function parseDateString(stringVal: string) {
    if (!stringVal || stringVal.replaceAll(/[^\d]/g, "").length === 0) {
        return null;
    }

    const timeArray = stringVal.split("");
    const todayTimeArray = format(startOfToday(), "dd.MM.yyyy HH:mm").split("");

    timeArray.forEach((char, i) => {
        if (char === "_") {
            timeArray[i] = todayTimeArray[i];
        }
    });

    const result = parse(timeArray.join(""), "dd.MM.yyyy HH:mm", startOfToday());
    return isValid(result) ? result : null;
}

const date = { ge: moment().startOf("day").toDate(), le: moment().endOf("day").toDate() };

export const defDatetimeRangeFilter = (name: string) => ({
    preparedFilter: { and: { [name]: date } },
    customFilters: { [name]: { id: name, value: date, filterType: "dateTimeRange" } },
});

export function formatUtcOffset(zone?: { utcOffset?: string; displayName?: string } | string | null) {
    const src = typeof zone === "string" ? zone : (zone && (zone.utcOffset || zone.displayName || "")) || "";
    return normalizeUtcOffset(src) || null;
}
